import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RotateCcw, Save, Languages, ArrowLeft, Layout, LayoutDashboard, Info, PhoneCall, ShoppingBag, FileText, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore } from '../store';
import { SEO } from '../components/SEO';
import AdminHeader from '../components/admin/AdminHeader';
import { toast } from 'sonner';

// Импортируем исходный объект статичных языковых файлов
import { translations } from '../i18n/translations';

const API = process.env.REACT_APP_BACKEND_URL;
const LANGS = [
  { code: 'en', label: 'EN' },
  { code: 'nl', label: 'NL' },
  { code: 'ro', label: 'RO' },
  { code: 'ru', label: 'RU' },
];

const SECTION_LABELS = {
  homepage: 'Homepage & Hero Block',
  hero: 'Homepage Hero Banner', 
  home: 'Homepage Body Content', 
  about: 'About Us Page Structure',
  b2bDashboard: 'B2B Dashboard Workspace',
  projectSpec: 'B2B Project Specification Module',
  b2b: 'B2B / Trade Program Portal', 
  catalogLabels: 'Catalog UI & Category Elements',
  nav: 'Navigation System Menu', 
  categories: 'Main Product Categories', 
  doorSubcategories: 'Door Subcategories',
  common: 'Common UI Layout / Buttons', 
  contactForms: 'Contact Info & Custom Briefs',
  contact: 'Contact Form Node labels', 
  customFurniture: 'Custom Furniture Brief',
  footer: 'Footer Navigation Layout', 
  cart: 'Quote Request / Retail Cart', 
  newsletter: 'Newsletter / Stay Informed', 
  auth: 'Login / Register Pages',
  chat: 'Concierge AI Chat Core', 
  lookbook: 'Lookbook Gallery Elements', 
  leadCapture: 'Lead Capture Modals',
  b2bDownloads: 'B2B Download Center Library',
  b2bApiKeys: 'B2B API Keys Management Hub',
  orderTracker: 'B2B Order Status Tracker',
  account: 'Client Personal Account Page',
  b2b_projects: 'B2B Projects Spec Labels',
  b2b_orders: 'B2B Order Tracking Labels'
};

const flattenCopyKeys = () => {
  try {
    const keys = [];
    const enDict = translations.en || {};
    Object.keys(enDict).forEach((section) => {
      const subObj = enDict[section];
      if (subObj && typeof subObj === 'object') {
        Object.keys(subObj).forEach((key) => {
          keys.push({
            path: `${section}.${key}`,
            section: section,
            base: subObj[key] !== undefined ? String(subObj[key]) : '',
          });
        });
      }
    });
    return keys;
  } catch (e) {
    console.error("Flattening translations failed", e);
    return [];
  }
};

const baseCopyValue = (lang, path) => {
  try {
    const dict = translations[lang] || translations.en || {};
    const [section, key] = String(path).split('.');
    const val = dict[section]?.[key];
    return val !== undefined ? String(val) : '';
  } catch (e) {
    return '';
  }
};

// Укрепленный изолированный премиум-компонент поля с вкладками
const BespokeCmsField = ({ k, working, setOverride, resetKey, aiTranslate, translatingKey }) => {
  const [activeTab, setActiveTab] = useState('en');
  
  if (!k || !k.path) return null;
  
  const safePath = String(k.path);
  const ov = working && working[safePath] ? working[safePath] : {};
  const edited = Object.keys(ov).length > 0;
  
  const pathString = safePath.toLowerCase();
  const baseString = k.base ? String(k.base) : '';
  
  const isTextArea = pathString.includes('desc') || 
                     pathString.includes('subtitle') || 
                     pathString.includes('text') ||
                     pathString.includes('notes') ||
                     pathString.includes('hint') ||
                     (baseString.length > 60);

  const currentTabCode = activeTab || 'en';
  
  let displayValue = '';
  if (ov && ov[currentTabCode] !== undefined && ov[currentTabCode] !== null) {
    displayValue = typeof ov[currentTabCode] === 'object' ? JSON.stringify(ov[currentTabCode]) : String(ov[currentTabCode]);
  }

  let safePlaceholder = 'Enter translation...';
  try {
    const placeholderVal = baseCopyValue(currentTabCode, safePath);
    if (placeholderVal) {
      safePlaceholder = placeholderVal;
    } else {
      safePlaceholder = `Enter translation for ${currentTabCode.toUpperCase()}...`;
    }
  } catch (e) {
    safePlaceholder = 'Enter translation...';
  }

  return (
    <div className="border border-neutral-200 p-5 bg-white space-y-3 rounded-none shadow-[0_2px_8px_rgba(0,0,0,0.01)] text-left">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-neutral-100 pb-2.5">
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-mono tracking-wider text-neutral-400 block uppercase mb-0.5">{safePath}</span>
          <span className="text-xs text-neutral-500 font-light italic block max-w-xl truncate">
            Default: {baseString}
          </span>
        </div>
        
        <div className="flex items-center gap-3 self-end sm:self-auto shrink-0">
          <button
            type="button"
            onClick={() => aiTranslate(safePath)}
            disabled={translatingKey === safePath}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-neutral-400 hover:text-black transition-colors disabled:opacity-40"
          >
            <Sparkles className="w-3 h-3 text-amber-500" strokeWidth={1.5} />
            {translatingKey === safePath ? '...' : 'AI Translate'}
          </button>
          
          <div className="flex bg-neutral-100 p-0.5 border border-neutral-200">
            {LANGS.map(lng => {
              const hasCustomValue = ov && ov[lng.code] && typeof ov[lng.code] === 'string' && ov[lng.code].trim() !== '';
              return (
                <button
                  key={lng.code}
                  type="button"
                  onClick={() => setActiveTab(lng.code)}
                  className={`px-2.5 py-1 text-[10px] font-bold tracking-wider transition-all duration-200 ${
                    currentTabCode === lng.code
                      ? 'bg-white text-black shadow-sm'
                      : hasCustomValue 
                        ? 'text-neutral-800 hover:text-black font-semibold' 
                        : 'text-neutral-400 hover:text-black'
                  }`}
                >
                  {lng.label}{hasCustomValue ? '*' : ''}
                </button>
              );
            })}
          </div>

          {edited && (
            <button
              type="button"
              onClick={() => resetKey(safePath)}
              className="p-1 text-neutral-400 hover:text-red-600 transition-colors"
              title="Reset overrides"
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>

      <div className="relative pt-1">
        {isTextArea ? (
          <textarea
            value={displayValue}
            placeholder={safePlaceholder}
            onChange={(e) => setOverride(safePath, currentTabCode, e.target.value)}
            className="w-full h-24 px-3 py-2 border border-neutral-200 focus:outline-none focus:border-black text-sm rounded-none text-neutral-900 font-light leading-relaxed resize-none bg-white"
          />
        ) : (
          <input
            type="text"
            value={displayValue}
            placeholder={safePlaceholder}
            onChange={(e) => setOverride(safePath, currentTabCode, e.target.value)}
            className="w-full h-11 px-3 border border-neutral-200 focus:outline-none focus:border-black text-sm rounded-none text-neutral-900 font-light bg-white"
          />
        )}
      </div>
    </div>
  );
};

const AdminContentPage = () => {
  // ФИКС: Все хуки маршрутизации, стейта и мемоизации подняты на самый верх, до любых условий return!
  const navigate = useNavigate();
  const tokenFromHook = useAuthStore((s) => s.token);
  const userFromHook = useAuthStore((s) => s.user);
  
  const [hydrated, setHydrated] = useState(false);
  const [working, setWorking] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedSection, setSelectedSection] = useState(null); 
  const [translatingKey, setTranslatingKey] = useState(null);
  const [translatingAll, setTranslatingAll] = useState(false);
  const [hoveredCardId, setHoveredCardId] = useState(null);

  const user = userFromHook || (hydrated ? useAuthStore.getState().user : null);
  const token = tokenFromHook || (hydrated ? useAuthStore.getState().token : null);

  const allKeys = useMemo(() => {
    const staticKeys = flattenCopyKeys();
    if (!working) return staticKeys;

    const merged = [...staticKeys];
    Object.keys(working).forEach((path) => {
      if (!merged.some((k) => k.path === path)) {
        const [section] = String(path).split('.');
        merged.push({
          path,
          section: section || 'common',
          base: working[path]?.en ? String(working[path].en) : 'Dynamic Database Field',
        });
      }
    });
    return merged;
  }, [working]);

  const sections = useMemo(() => {
    const map = { homepage: [], about: [], b2bDashboard: [], projectSpec: [], catalogLabels: [], contactForms: [] };
    
    allKeys.forEach((k) => {
      const secStr = String(k.section);
      if (secStr === 'hero' || secStr === 'home' || secStr === 'newsletter') {
        map.homepage.push(k);
      } else if (secStr === 'about') {
        map.about.push(k);
      } else if (secStr === 'b2bDashboard' || secStr === 'b2bDownloads' || secStr === 'b2bApiKeys' || secStr === 'orderTracker' || secStr === 'auth') {
        map.b2bDashboard.push(k);
      } else if (secStr === 'projectSpec' || secStr === 'b2b_projects' || secStr === 'b2b_orders') {
        map.projectSpec.push(k);
      } else if (secStr === 'nav' || secStr === 'categories' || secStr === 'doorSubcategories' || secStr === 'common' || secStr === 'footer' || secStr === 'cart' || secStr === 'lookbook') {
        map.catalogLabels.push(k);
      } else if (secStr === 'contact' || secStr === 'customFurniture' || secStr === 'chat' || secStr === 'leadCapture' || secStr === 'account') {
        map.contactForms.push(k);
      } else {
        if (!map[secStr]) map[secStr] = [];
        map[secStr].push(k);
      }
    });
    return map;
  }, [allKeys]);

  const dashboardCards = useMemo(() => [
    { id: 'homepage', title: 'Homepage & Hero Banner', desc: 'Главный баннер, концепция One Space, рассылка и базовые CTA-элементы.', icon: Layout, nodes: sections.homepage?.length || 0 },
    { id: 'about', title: 'About Us Layout', desc: 'История бренда, философия ателье, метрики фабрики и закрытый манифест.', icon: Info, nodes: sections.about?.length || 0 },
    { id: 'b2bDashboard', title: 'B2B Professional Hub', desc: 'Панель архитекторов, трейд-калькуляция, управление API-ключами, авторизация.', icon: LayoutDashboard, nodes: sections.b2bDashboard?.length || 0 },
    { id: 'projectSpec', title: 'B2B Project Specifications', desc: 'Интерфейс интерактивной ведомости объекта, кастомные поля профилей, стекол и высот.', icon: FileText, nodes: sections.projectSpec?.length || 0 },
    { id: 'catalogLabels', title: 'Catalog UI & Category Labels', desc: 'Названия коллекций, подкатегории дверей, системные кнопки фильтрации и навигация.', icon: ShoppingBag, nodes: sections.catalogLabels?.length || 0 },
    { id: 'contactForms', title: 'Contact Info & Custom Briefs', desc: 'Адреса представительств, формы обратной связи, ИИ-чат и бриф на мебель под заказ.', icon: PhoneCall, nodes: sections.contactForms?.length || 0 },
  ], [sections]);

  const currentCard = useMemo(() => {
    return dashboardCards.find(c => c.id === selectedSection) || { title: 'Section Blueprint', desc: 'Manage localized fields.' };
  }, [selectedSection, dashboardCards]);

  const activeFields = useMemo(() => {
    if (!selectedSection) return [];
    return sections[selectedSection] || [];
  }, [selectedSection, sections]);

  useEffect(() => { const id = setTimeout(() => setHydrated(true), 300); return () => clearTimeout(id); }, []);
  
  useEffect(() => {
    if (!hydrated) return;
    const u = useAuthStore.getState().user;
    if (!u || u.role !== 'admin') {
      navigate('/admin', { replace: true });
    }
  }, [hydrated, navigate]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/copy`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (alive) { setWorking(data.overrides || {}); setLoading(false); }
      } catch (e) { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  // СЛЕДУЮЩИЙ ШАГ: Логика досрочного рендеринга лоадеров теперь безопасно находится ПОД всеми хуками
  if (!hydrated || loading) return <div className="min-h-screen flex items-center justify-center text-xs text-gray-400 bg-[#FAFAFA] font-light tracking-wide">Loading premium text core…</div>;
  if (!user || user.role !== 'admin' || !working) return null;

  const setOverride = (path, lang, value) => setWorking((w) => ({ ...w, [path]: { ...(w[path] || {}), [lang]: value } }));
  const resetKey = (path) => setWorking((w) => { const n = { ...w }; delete n[path]; return n; });

  const aiTranslate = async (path) => {
    const ov = working[path] || {};
    const sourceEn = (ov.en && String(ov.en).trim()) ? String(ov.en).trim() : baseCopyValue('en', path);
    if (!sourceEn) { toast.error('Add English text first'); return; }
    const targets = ['nl', 'ro', 'ru'].filter((l) => !((ov[l] || '').trim()));
    if (targets.length === 0) { toast.info('NL, RO & RU are already filled — clear a field to re-translate'); return; }
    setTranslatingKey(path);
    try {
      const res = await fetch(`${API}/api/admin/translate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: sourceEn, targets }),
      });
      if (!res.ok) throw new Error('Translation failed');
      const data = await res.json();
      setWorking((w) => {
        const cur = { ...(w[path] || {}) };
        if (!cur.en) cur.en = sourceEn; 
        targets.forEach((l) => { const tr = (data.translations?.[l] || '').trim(); if (tr) cur[l] = tr; });
        return { ...w, [path]: cur };
      });
      toast.success(`Translated to ${targets.map((l) => l.toUpperCase()).join(', ')}`);
    } catch (e) { toast.error(e.message || 'Translation failed'); } finally { setTranslatingKey(null); }
  };

  const translateAllEdited = async () => {
    const pending = Object.entries(working).filter(([, v]) => (v?.en || '').trim() && ['nl', 'ro', 'ru'].some((l) => !((v[l] || '').trim())));
    if (pending.length === 0) { toast.info('No edited English blocks need translating'); return; }
    setTranslatingAll(true);
    for (const [path, v] of pending) {
      const targets = ['nl', 'ro', 'ru'].filter((l) => !((v[l] || '').trim()));
      try {
        const res = await fetch(`${API}/api/admin/translate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ text: v.en.trim(), targets }),
        });
        if (!res.ok) continue;
        const data = await res.json();
        setWorking((w) => {
          const cur = { ...(w[path] || {}) };
          targets.forEach((l) => { const tr = (data.translations?.[l] || '').trim(); if (tr) cur[l] = tr; });
          return { ...w, [path]: cur };
        });
      } catch (e) { /* skip */ }
    }
    setTranslatingAll(false);
    toast.success('AI Translation sequence executed successfully');
  };

  const save = async () => {
    setSaving(true);
    const pruned = {};
    Object.entries(working).forEach(([path, langs]) => {
      const clean = {};
      Object.entries(langs || {}).forEach(([l, v]) => { if (typeof v === 'string') clean[l] = v; });
      if (Object.keys(clean).length > 0) pruned[path] = clean;
    });
    try {
      const res = await fetch(`${API}/api/admin/copy`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ overrides: pruned }),
      });
      if (!res.ok) throw new Error('Save failed');
      setWorking(pruned);
      window.dispatchEvent(new CustomEvent('site-copy-updated'));
      toast.success('Site copy saved & published');
    } catch (e) { toast.error(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  const filtered = search && String(search).trim()
    ? allKeys.filter((k) => String(k.path).toLowerCase().includes(search.toLowerCase()) || String(k.base).toLowerCase().includes(search.toLowerCase()))
    : null;

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-28 text-left font-light">
      <SEO title="Site Copy · Admin" description="Edit site text" path="admin/content" />
      <AdminHeader active="content" />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        
        {selectedSection && !filtered && (
          <button
            type="button"
            onClick={() => setSelectedSection(null)}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-black font-semibold mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Content Hub
          </button>
        )}

        <div className="mb-10">
          <p className="text-[10px] font-medium tracking-[0.2em] uppercase text-neutral-400 mb-2 font-mono">CMS · Interface Engine</p>
          <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-black">
            {selectedSection && !filtered ? currentCard.title : 'Structure & Content Hub'}
          </h1>
          <p className="text-sm font-light text-neutral-500 mt-4 max-w-2xl leading-relaxed">
            {selectedSection && !filtered 
              ? currentCard.desc 
              : 'Управляйте мультиязычными текстами, интерфейсными метками и кастомными описаниями во всех разделах экосистемы FEDYK Furniture.'}
          </p>
        </div>

        <div className="relative mb-10 shadow-[0_4px_12px_rgba(0,0,0,0.01)]">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" strokeWidth={1.5} />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Поиск ключевых слов по всей базе текстовых нод (например: dimensions, b2b)..."
            className="w-full h-12 pl-12 pr-4 border border-neutral-200 bg-white text-sm focus:outline-none focus:border-black transition-colors rounded-none text-neutral-900 placeholder:text-neutral-400 font-light" />
        </div>

        {filtered ? (
          <div className="space-y-4">
            <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-neutral-400">{filtered.length} matches found across text base</p>
            {filtered.map((k, idx) => (
              <BespokeCmsField 
                key={`${k.path}-${idx}`} k={k} working={working} setOverride={setOverride} 
                resetKey={resetKey} aiTranslate={aiTranslate} translatingKey={translatingKey} 
              />
            ))}
          </div>
        ) : selectedSection ? (
          <div className="space-y-6">
            <div className="p-4 bg-neutral-100 border border-neutral-200 text-[10px] uppercase font-bold text-neutral-500 tracking-widest font-mono">
              Section: {SECTION_LABELS[selectedSection] || selectedSection} · {activeFields.length} active modules
            </div>
            <div className="space-y-4">
              {activeFields.map((k, idx) => (
                <BespokeCmsField 
                  key={`${k.path}-${idx}`} k={k} working={working} setOverride={setOverride} 
                  resetKey={resetKey} aiTranslate={aiTranslate} translatingKey={translatingKey} 
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {dashboardCards.map((card) => {
              const IconComponent = card.icon;
              const isHovered = hoveredCardId === card.id;
              
              return (
                <div
                  key={card.id}
                  onClick={() => setSelectedSection(card.id)}
                  onMouseEnter={() => setHoveredCardId(card.id)}
                  onMouseLeave={() => setHoveredCardId(null)}
                  className="border p-6 transition-all duration-300 rounded-none cursor-pointer flex flex-col justify-between h-52 relative shadow-sm"
                  style={{
                    backgroundColor: isHovered ? '#0A0A0A' : '#FFFFFF',
                    borderColor: isHovered ? '#0A0A0A' : '#ECECEC'
                  }}
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div 
                        className="p-2.5 border transition-colors duration-300 rounded-none"
                        style={{
                          backgroundColor: isHovered ? '#1A1A1A' : '#F5F5F5',
                          borderColor: isHovered ? '#2A2A2A' : '#ECECEC',
                          color: isHovered ? '#FFFFFF' : '#0A0A0A'
                        }}
                      >
                        <IconComponent className="w-4 h-4" strokeWidth={1.25} />
                      </div>
                      <span 
                        className="text-[10px] font-mono tracking-wider transition-colors duration-300"
                        style={{ color: isHovered ? '#9E9E9E' : '#737373' }}
                      >
                        {card.nodes} fields
                      </span>
                    </div>
                    
                    <h3 
                      className="text-lg font-medium tracking-tight transition-colors duration-300"
                      style={{ color: isHovered ? '#FFFFFF' : '#0A0A0A' }}
                    >
                      {card.title}
                    </h3>
                    
                    <p 
                      className="text-xs font-light mt-2 leading-relaxed max-w-sm transition-colors duration-300"
                      style={{ color: isHovered ? '#E5E5E5' : '#404040' }}
                    >
                      {card.desc}
                    </p>
                  </div>

                  <div 
                    className="text-[10px] uppercase font-semibold tracking-widest pt-4 flex items-center gap-1.5 transition-colors duration-300"
                    style={{ color: isHovered ? '#FFFFFF' : '#0A0A0A' }}
                  >
                    Configure Layout <span>→</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-neutral-200 bg-white/95 backdrop-blur-md z-50 shadow-[0_-8px_30px_rgba(0,0,0,0.02)]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={translateAllEdited} 
            disabled={translatingAll || saving} 
            className="h-10 bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-all font-medium uppercase tracking-wider"
            style={{ height: '40px' }}
          >
            <Languages className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
            {translatingAll ? 'Translating…' : 'AI Translate Edited'}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={save} 
            disabled={saving || translatingAll} 
            className="h-10 bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-all font-medium uppercase tracking-wider"
            style={{ height: '40px' }}
          >
            <Save className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
            {saving ? 'Publishing…' : 'Save & Publish Layout'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminContentPage;
